import { queryOptions } from "@tanstack/react-query"
import { isAxiosError } from "axios"
import { posthog } from "posthog-js"
import { safeQueryOptions } from "@src/utils/query"
import { client } from "./client"
import { components } from "./schema"
import { UnpackPromise, UnpackArray } from "@src/utils/types"

export const isTosException = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false
  return (
    error.response?.status === 500 &&
    (error.response?.data as { error?: string })?.error ===
      "UserMustAcceptNewTermsAndConditionsException"
  )
}

// Backend returns 204 (no body) when the JWT is well-formed but rejected
// (e.g. user no longer exists, token revoked). Treat as an auth failure so
// the loader can log the user out.
export class InvalidTokenError extends Error {
  constructor() {
    super("GetCurrent returned no content — token rejected by backend")
    this.name = "InvalidTokenError"
  }
}

export const acceptTermsAndConditions = async () => {
  await client.PUT("/api/User/AcceptTermsAndConditions")
}

export const userKeys = {
  all: ["users"],
  list: () => [...userKeys.all, "list"],
  adminList: () => [...userKeys.all, "admin-list"],
  current: () => [...userKeys.all, "current"],
  currentStuff: () => [...userKeys.current(), "stuff"],
  communities: () => [...userKeys.all, "current", "communities"],
  singleProfile: (id?: number) => [...userKeys.all, "profile", id],
  usersList: (id?: number) => [...userKeys.all, "list", "community", id],
  search: (filter?: UserSearchFilter) => [...userKeys.all, "search", filter],
  activateAccount: (code?: string | null) => [
    ...userKeys.all,
    "activate-account",
    code,
  ],
}

export type LoginRequest = components["schemas"]["LoginRequestDto"]
export type SignupRequest = components["schemas"]["SignUpDto"]

export type TokenResponse = {
  access_token: string
  username: string
  token_type: string
}

// No-op while PostHog is opted out (cookies not accepted), so useCookieConsent
// calls this again right after opting in.
export const identifyPostHogUser = (
  user?: Pick<components["schemas"]["UserCurrentDto"], "id" | "mail" | "name">,
) => {
  if (user?.id) {
    posthog.identify(String(user.id), { email: user.mail, name: user.name })
  }
}

export const getCurrentUser = async () => {
  const data = await client.GET("/api/User/GetCurrent")

  if (!data) {
    throw new InvalidTokenError()
  }

  // Every login path ends up here (auth routes and the signup dialog on public
  // apply pages), so this is the one place that reliably ties the anonymous
  // PostHog session to the logged-in user.
  identifyPostHogUser(data)

  return data
}

export type CurrentUser = UnpackPromise<ReturnType<typeof getCurrentUser>>

export const getCurrentUserQuery = () =>
  queryOptions({
    queryKey: userKeys.current(),
    queryFn: getCurrentUser,
    // 24 hour cache time
    gcTime: 1000 * 60 * 60 * 24,
    // 1 minute stale time (time before background refresh)
    staleTime: 1000 * 60,
    retry: (failureCount, error) => {
      if (isTosException(error)) return false
      if (error instanceof InvalidTokenError) return false
      return failureCount < 3
    },
  })

export const getCurrentUserCommunities = async () => {
  const data = await client.GET("/api/User/GetMyCommunities")

  return data
}

export type UserCommunitiesList = UnpackPromise<
  ReturnType<typeof getCurrentUserCommunities>
>
export type UserCommunities = UnpackArray<UserCommunitiesList>

export const getCurrentUserCommunitiesQuery = () =>
  queryOptions({
    queryKey: userKeys.communities(),
    queryFn: getCurrentUserCommunities,
  })

export const getToken = async (
  request: LoginRequest,
): Promise<TokenResponse> => {
  const data = await client.POST("/token", {
    body: request,
  })

  return data as unknown as TokenResponse
}

export const getCurrentUserStuff = async () => {
  const data = await client.GET("/api/User/GetMyStuff")

  return data
}

export type CurrentUserStuff = UnpackPromise<
  ReturnType<typeof getCurrentUserStuff>
>

export const getCurrentUserStuffQuery = () =>
  queryOptions({
    queryKey: userKeys.currentStuff(),
    queryFn: getCurrentUserStuff,
    // 24 hour cache time
    gcTime: 1000 * 60 * 60 * 24,
    // 1 minute stale time (time before background refresh)
    staleTime: 1000 * 60,
  })

export const getUserProfile = async (id: number) => {
  const data = await client.GET("/api/User/GetProfile", {
    params: {
      query: {
        id,
      },
    },
  })

  return data
}

export type UserProfile = UnpackPromise<ReturnType<typeof getUserProfile>>

export const getUserProfileQuery = (id?: number) =>
  safeQueryOptions({
    queryKeys: userKeys.singleProfile(id),
    queryFn: getUserProfile,
    unsafeQueryFnArgs: [id],
  })

export type ExternalUserInvitationRequest =
  components["schemas"]["ExternalUserInvitationDto"]

export const inviteToProjectViaEmail = async (
  inviteRequest: ExternalUserInvitationRequest,
) => {
  const data = await client.POST("/api/Project/InviteToProjectViaEmail", {
    body: inviteRequest,
  })

  return data
}

export const inviteToCommunityViaEmail = async (
  inviteRequest: ExternalUserInvitationRequest,
) => {
  const data = await client.POST("/api/User/InviteToCommunityViaEmail", {
    body: inviteRequest,
  })

  return data
}

export const inviteToTabEntityViaEmail = async (
  inviteRequest: ExternalUserInvitationRequest,
) => {
  const data = await client.POST("/api/TabEntity/InviteToTabEntityViaEmail", {
    body: inviteRequest,
  })

  return data
}

export const joinCommunityViaInviteLink = async (joinRequest: {
  communityId: number
  circleId: number
  tags?: string[] | null
}) => {
  await client.POST("/api/User/JoinCommunityViaInviteLink", {
    body: joinRequest,
  })
}

export const followUser = async (userId: number, communityId?: number) => {
  await client.GET("/api/User/Follow", {
    params: {
      query: {
        id: userId,
        ...(communityId !== undefined && { communityId }),
      },
    },
  })
}

export const unfollowUser = async (userId: number, communityId?: number) => {
  await client.GET("/api/User/Unfollow", {
    params: {
      query: {
        id: userId,
        ...(communityId !== undefined && { communityId }),
      },
    },
  })
}

export const changeUserProjectCircle = async (
  userId: number,
  projectId: number,
  projectCircleId: number,
  communityId?: number,
) => {
  await client.GET("/api/Project/EditProjectCircle", {
    params: {
      query: {
        userId,
        projectId,
        projectCircleId,
        ...(communityId !== undefined && { communityId }),
      },
    },
  })
}

export const changeUserCommunityCircle = async (
  userId: number,
  communityId: number,
  communityCircleId: number,
) => {
  await client.GET("/api/Community/EditCommunityCircle", {
    params: {
      query: {
        userId,
        communityId,
        communityCircleId,
      },
    },
  })
}

export const removeUserFromProject = async (
  userId: number,
  projectId: number,
  communityId?: number,
) => {
  await client.GET("/api/Project/AbandonProject", {
    params: {
      query: {
        userId,
        projectId,
        ...(communityId !== undefined && { communityId }),
      },
    },
  })
}

// The backend returns the full User entity from /User/SignUp but the OpenAPI
// schema declares no response body. We only need isActive to know whether the
// account was already activated (invited users come back active and skip the
// "check your email" step).
export type SignupResponse = { isActive: boolean } | undefined

export const userSignup = async (payload: SignupRequest) => {
  const result = await client.POST("/api/User/SignUp", { body: payload })
  return result as unknown as SignupResponse
}

export const removeUserFromCommunity = async (
  userId: number,
  communityId: number,
) => {
  await client.GET("/api/Community/AbandonCommunity", {
    params: {
      query: {
        userId,
        communityId,
      },
    },
  })
}

export const userSignupViaApplication = async (
  payload: SignupRequest,
  applicationDefinitionId: number,
) => {
  await client.POST("/api/User/SignUpViaApplication", {
    body: payload,
    params: {
      query: {
        appId: applicationDefinitionId,
      },
    },
  })
}

export const removeUserFromTabEntity = async (
  userId: number,
  entityId: number,
) => {
  await client.POST("/api/TabEntity/{id}/AbandonTabEntity", {
    params: {
      path: {
        id: entityId,
      },
      query: {
        userId,
      },
    },
  })
}

export type UserSearchFilter = components["schemas"]["UserSearchFilter"]

export type UserForInviteDtoResultListDto =
  components["schemas"]["UserForInviteDtoResultListDto"]

export type UserForInviteDto = components["schemas"]["UserForInviteDto"]

export const searchUsers = async (filter: UserSearchFilter) => {
  const data = await client.POST("/api/User/Search", {
    body: filter,
  })

  return data as unknown as UserForInviteDtoResultListDto
}

export const searchUsersQuery = (filter: UserSearchFilter) =>
  queryOptions({
    queryFn: () => searchUsers(filter),
    queryKey: userKeys.search(filter),
  })

export const resetPassword = async (email: string) => {
  const data = await client.GET("/api/User/SendResetPasswordMail", {
    params: {
      query: {
        mail: email,
      },
    },
  })

  return data
}

export const activateAccount = async (code: string) => {
  const data = await client.GET("/api/User/Activate", {
    params: {
      query: {
        code,
      },
    },
  })

  return data
}

export const activateAccountQuery = (code?: string | null) => ({
  ...safeQueryOptions({
    queryFn: activateAccount,
    unsafeQueryFnArgs: [code],
    queryKeys: userKeys.activateAccount(code),
  }),
  retry: false,
  refetchOnMount: false,
})

export const confirmResetPassword = async (
  activationCode: string,
  password: string,
) => {
  const data = await client.GET("/api/User/ResetPassword", {
    params: {
      query: {
        activationCode,
        password,
      },
    },
  })

  return data
}

export type EditUserTagsDto = components["schemas"]["EditUserTagsDto"]

export const saveUserTags = async (request: EditUserTagsDto) => {
  await client.POST("/api/User/SaveTags", {
    body: request,
  })
}

export type SaveUserProfileDto = components["schemas"]["SaveUserProfileDto"]

export const saveUserProfile = async (request: SaveUserProfileDto) => {
  await client.POST("/api/User/SaveProfile", {
    body: request,
  })
}

export type ChangePasswordDto = components["schemas"]["ChangePasswordDto"]

// Changing a password revokes every session for the user, including the one making
// this call, so the endpoint returns a replacement token for the acting browser.
// The body is absent when nothing was rotated (an admin changing another user's
// password).
//
// TokenResponse, not schema.ts's LoginResponseDto: the C# DTO carries
// [JsonProperty("access_token")], so the wire format is snake_case. Swagger reads the
// property name and not the attribute, so the generated type says accessToken and is
// wrong — the same reason getToken above hand-rolls this shape.
export const changePassword = async (request: ChangePasswordDto) => {
  const data = await client.POST("/api/User/ChangePassword", {
    body: request,
  })

  return data as unknown as TokenResponse | undefined
}

export type EmailEditDto = components["schemas"]["EmailEeditDto"]

export const editMyEmail = async (request: EmailEditDto) => {
  await client.POST("/api/User/EditMyEmail", {
    body: request,
  })
}

export const disableMe = async () => {
  await client.GET("/api/User/DisableMe")
}

export const requestPersonalInformation = async () => {
  const response = await client.axiosInstance.get(
    "/api/UserPersonalInformation/RequestPersonalInformation",
    { responseType: "blob" },
  )
  return response.data
}

// Admin functions
export const USER_ACCOUNT_STATES = [
  "Active",
  "Pending",
  "Disabled",
  "Banned",
] as const
export type UserAccountState = (typeof USER_ACCOUNT_STATES)[number]

export type User = components["schemas"]["User"]
export type UserForListDto = components["schemas"]["UserForListDto"] & {
  isPlatformAdmin?: boolean
  primaryCommunityName?: string | null
  isCommunityAdmin?: boolean
  accountState?: UserAccountState | null
}
export type UserListDto = {
  count: number
  list: UserForListDto[]
}
export type UserHistoryDataDto = components["schemas"]["UserHistoryDataDto"]
export type ExternalUserPrefilledInvitationDto =
  components["schemas"]["ExternalUserPrefilledInvitationDto"]

export type GetAllUsersFilter = {
  skip?: number
  take?: number
  name?: string
}

export const getAllUsers = async (filter: GetAllUsersFilter = {}) => {
  const response = await client.axiosInstance.post("/api/User/GetAll", filter)
  return response.data as UserListDto
}

export const getAllUsersQuery = (filter: GetAllUsersFilter = {}) =>
  queryOptions({
    queryKey: [...userKeys.list(), filter],
    queryFn: () => getAllUsers(filter),
  })

export type GetAllSuperAdminFilter = {
  searchTerm?: string
  communityId?: number
  isCommunityAdmin?: boolean
  accountStates?: UserAccountState[]
  skip?: number
  take?: number
}

export const getAllSuperAdminUsers = async (
  filter: GetAllSuperAdminFilter = {},
) => {
  const response = await client.axiosInstance.post(
    "/api/User/GetAllSuperAdmin",
    filter,
  )
  return response.data as UserListDto
}

export const getAllSuperAdminUsersQuery = (
  filter: GetAllSuperAdminFilter = {},
) =>
  queryOptions({
    queryKey: [...userKeys.adminList(), filter],
    queryFn: () => getAllSuperAdminUsers(filter),
  })

export type SuperAdminCommunityMembership = {
  communityId: number
  communityName?: string | null
  circleName?: string | null
  isAdmin: boolean
  isPrimary: boolean
  status?: string | null
}

export type SuperAdminProjectMembership = {
  projectId: number
  projectName?: string | null
  circleName?: string | null
  status?: string | null
  communityId?: number | null
  communityName?: string | null
  isDisabled: boolean
}

export type SuperAdminUserMemberships = {
  communities: SuperAdminCommunityMembership[]
  projects: SuperAdminProjectMembership[]
}

export const getSuperAdminUserMemberships = async (userId: number) => {
  const response = await client.axiosInstance.get(
    "/api/User/GetSuperAdminUserMemberships",
    { params: { userId } },
  )
  return response.data as SuperAdminUserMemberships
}

export const getSuperAdminUserMembershipsQuery = (userId: number) =>
  queryOptions({
    queryKey: [...userKeys.adminList(), "memberships", userId],
    queryFn: () => getSuperAdminUserMemberships(userId),
    enabled: userId > 0,
  })

export const enableUser = async (id: number) => {
  await client.GET("/api/User/EnableUser", {
    params: {
      query: {
        id,
      },
    },
  })
}

export const banUser = async (id: number) => {
  await client.GET("/api/User/BanUser", {
    params: {
      query: {
        id,
      },
    },
  })
}

export const disableUser = async (id: number) => {
  await client.GET("/api/User/DisableUser", {
    params: {
      query: {
        id,
      },
    },
  })
}

export const togglePlatformAdmin = async (id: number) => {
  await client.GET("/api/User/TogglePlatformAdmin", {
    params: {
      query: {
        id,
      },
    },
  })
}

export const getUserHistoryData = async (id: number) => {
  const data = await client.GET("/api/UserHistoryData/Get", {
    params: {
      query: {
        id,
      },
    },
  })
  return data
}

export type BulkInvitee = ExternalUserPrefilledInvitationDto

export const signUpImportUsers = async (invitees: BulkInvitee[]) => {
  await client.POST("/api/User/SignUpImport", {
    body: invitees,
  })
}

export const createBulkUsers = async (
  invitees: BulkInvitee[],
  onProgress?: (completed: number, failed: number) => void,
) => {
  let completed = 0
  let failed = 0

  const results = await Promise.all(
    invitees.map(async (invitee) => {
      try {
        await client.POST("/api/User/SignUpImport", {
          body: [invitee],
        })
        completed++
        onProgress?.(completed, failed)
        return { success: true, email: invitee.email }
      } catch (error) {
        failed++
        onProgress?.(completed, failed)
        return { success: false, email: invitee.email, error }
      }
    }),
  )

  return results
}

export const impersonateUser = async (
  userId: number,
): Promise<TokenResponse> => {
  const response = await client.axiosInstance.post(
    `/token/impersonate/${userId}`,
  )
  return response.data as TokenResponse
}

export const uploadUserUpdateCsv = async (file: File, communityId: number) => {
  const formData = new FormData()
  formData.append("file", file)

  const response = await client.axiosInstance.post(
    `/api/File/UserUpdateCsv/${communityId}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  )
  return response.data
}
